import React, { useState, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './dialog'
import { Button } from './button'
import { Label } from './label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select"
import { BarChart3, LineChart, PieChart, X, Table, Eye, EyeOff } from 'lucide-react'
import { ColumnType } from '../../lib/smartFormatter'
import { DataTableViewer } from './DataTableViewer'
import UniversalChartRenderer from '../charts/UniversalChartRenderer'
import ColorPaletteSelector from './ColorPaletteSelector'
import { useAuth } from '../../contexts/AuthContext'
import { useAtom } from 'jotai'
import { currentOrganizationAtom } from '../../store/organizationAtoms'
import { getChartColors } from '../../lib/chartUtils'
import { saveChartFromConfig } from '../../lib/chartSaveUtil'
import { useToast } from '../../hooks/use-toast'

// Helper function to convert text to title case
const toTitleCase = (str: string): string => {
  return str.replace(/\w\S*/g, (txt) => 
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  )
}

// Helper function to prepare chart data for preview
const prepareChartData = (
  data: Record<string, any>[], 
  xAxis: string, 
  yAxis: string, 
  groupBy?: string
) => {
  if (!xAxis || !yAxis || data.length === 0) return []
  
  console.log('prepareChartData called with:', { xAxis, yAxis, groupBy, dataLength: data.length })
  
  // For chart preview, don't limit data points when we need to aggregate by categories
  // The aggregation will naturally reduce the data to a manageable size
  let processedData = data
  
  if (groupBy && groupBy !== '__none__' && groupBy !== '') {
    console.log('Processing grouped data with groupBy:', groupBy)
    
    // For grouped data, create proper multi-series structure
    const aggregated: Record<string, Record<string, number>> = {}
    
    processedData.forEach(row => {
      const xValue = String(row[xAxis])
      const groupValue = String(row[groupBy])
      const yValue = Number(row[yAxis]) || 0
      
      if (!aggregated[xValue]) {
        aggregated[xValue] = {}
      }
      
      if (!aggregated[xValue][groupValue]) {
        aggregated[xValue][groupValue] = 0
      }
      
      aggregated[xValue][groupValue] += yValue
    })
    
    // Convert to multi-series chart format that UniversalChartRenderer can handle
    const chartData = Object.keys(aggregated).map(xValue => {
      const result: any = { name: formatAxisLabel(xValue) }
      Object.keys(aggregated[xValue]).forEach(groupValue => {
        result[groupValue] = aggregated[xValue][groupValue]
      })
      return result
    })
    
    console.log('Grouped chart data (multi-series):', chartData)
    return chartData
  } else {
    // Simple x-axis vs y-axis mapping with aggregation for duplicate x-values
    const aggregated: Record<string, number> = {}
    
    processedData.forEach(row => {
      const xValue = String(row[xAxis])
      const yValue = Number(row[yAxis]) || 0
      
      if (!aggregated[xValue]) {
        aggregated[xValue] = 0
      }
      
      aggregated[xValue] += yValue
    })
    
    // Convert to chart format
    const chartData = Object.keys(aggregated).map(xValue => ({
      name: formatAxisLabel(xValue),
      value: aggregated[xValue]
    }))
    
    console.log('Simple chart data (aggregated):', chartData)
    return chartData
  }
}

// Helper function to format axis labels to prevent overcrowding
const formatAxisLabel = (label: string): string => {
  // If it's a date, try to format it shorter
  if (label.match(/^\d{4}-\d{2}-\d{2}/) || label.match(/^\d{1,2}\/\d{1,2}\/\d{4}/)) {
    try {
      const date = new Date(label)
      return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
    } catch {
      return label
    }
  }
  
  // If it's a long string, truncate it
  if (label.length > 10) {
    return label.substring(0, 8) + '...'
  }
  
  return label
}

interface ChartConfigurationDialogProps {
  isOpen: boolean
  onClose: () => void
  data: Record<string, any>[]
  headers: string[]
  columnTypes: Record<string, ColumnType>
  chartType: 'bar' | 'line' | 'pie'
  onCreateChart: (config: ChartConfig) => void
}

interface ChartConfig {
  chartType: 'bar' | 'line' | 'pie'
  xAxis: string
  yAxis: string
  groupBy?: string
  title: string
  colorPalette?: string  // Selected color palette ID
  showLegend?: boolean  // Whether to show legend
  // Enhanced fields for multi-series support
  chartData?: any[]  // Processed chart data
  isMultiSeries?: boolean  // Whether this is a grouped chart
  seriesInfo?: {
    keys: string[]  // Series names (e.g., ['Widget A', 'Widget B'])
    colors: string[]  // Colors for each series
  }
}

const chartTypeConfig = {
  bar: {
    icon: BarChart3,
    label: 'Bar Chart',
    description: 'Compare values across categories',
    color: '#FF8635'
  },
  line: {
    icon: LineChart,
    label: 'Line Chart', 
    description: 'Show trends over time',
    color: '#B7BBC6'
  },
  pie: {
    icon: PieChart,
    label: 'Pie Chart',
    description: 'Show proportions of a whole',
    color: '#E6717D'
  }
}

export function ChartConfigurationDialog({
  isOpen,
  onClose,
  data,
  headers,
  columnTypes,
  chartType,
  onCreateChart
}: ChartConfigurationDialogProps) {
  const [xAxis, setXAxis] = useState<string>('')
  const [yAxis, setYAxis] = useState<string>('')
  const [groupBy, setGroupBy] = useState<string>('')
  const [title, setTitle] = useState<string>('')
  const [colorPalette, setColorPalette] = useState<string>('fireforge')
  const [initialized, setInitialized] = useState(false)
  const [viewMode, setViewMode] = useState<'chart' | 'data'>('chart')
  const [showLegend, setShowLegend] = useState<boolean>(true)
  
  // Auth and organization context
  const { currentUser } = useAuth()
  const [currentOrganization] = useAtom(currentOrganizationAtom)
  const { toast } = useToast()

  const config = chartTypeConfig[chartType]
  const ChartIcon = config.icon
  
  // Memoized chart data that updates when inputs change
  const chartData = React.useMemo(() => {
    console.log('Chart data updating due to change in:', { xAxis, yAxis, groupBy })
    return prepareChartData(data, xAxis, yAxis, groupBy)
  }, [data, xAxis, yAxis, groupBy])
  
  // Debug current state
  React.useEffect(() => {
    console.log('Current state:', { xAxis, yAxis, groupBy, canCreate: canCreateChart() })
    console.log('Chart data result:', chartData)
  }, [xAxis, yAxis, groupBy, chartData])

  // Reset initialization when dialog opens
  React.useEffect(() => {
    if (isOpen) {
      setInitialized(false)
      setXAxis('')
      setYAxis('')
      setGroupBy('')
      setTitle('')
      setColorPalette('fireforge')
      setViewMode('chart')
      setShowLegend(true)
    }
  }, [isOpen])

  // Categorize columns by type for better suggestions
  const columnsByType = useMemo(() => {
    const categories = {
      text: [] as string[],
      numeric: [] as string[],
      date: [] as string[],
      all: headers
    }

    headers.forEach(header => {
      const columnType = columnTypes[header]?.type || 'text'
      if (['currency', 'number', 'percentage'].includes(columnType)) {
        categories.numeric.push(header)
      } else if (columnType === 'date') {
        categories.date.push(header)
      } else {
        categories.text.push(header)
      }
    })

    return categories
  }, [headers, columnTypes])

  // Smart defaults based on chart type
  React.useEffect(() => {
    if (!initialized && columnsByType.all.length > 0) {
      let defaultXAxis = ''
      let defaultYAxis = ''
      
      if (chartType === 'line' && columnsByType.date.length > 0) {
        defaultXAxis = columnsByType.date[0]
      } else if (columnsByType.text.length > 0) {
        defaultXAxis = columnsByType.text[0]
      } else if (columnsByType.all.length > 0) {
        // Fallback to first available column
        defaultXAxis = columnsByType.all[0]
      }

      if (columnsByType.numeric.length > 0) {
        defaultYAxis = columnsByType.numeric[0]
      } else if (columnsByType.all.length > 0) {
        // Fallback to first non-xAxis column
        const availableColumns = columnsByType.all.filter(col => col !== defaultXAxis)
        defaultYAxis = availableColumns.length > 0 ? availableColumns[0] : columnsByType.all[0]
      }

      setXAxis(defaultXAxis)
      setYAxis(defaultYAxis)
      
      // Generate smart title in title case
      const yLabel = defaultYAxis || 'Value'
      const xLabel = defaultXAxis || 'Category'
      setTitle(toTitleCase(`${yLabel} by ${xLabel}`))
      
      setInitialized(true)
    }
  }, [chartType, columnsByType, initialized])

  const canCreateChart = () => {
    const hasValidXAxis = xAxis && xAxis !== "__placeholder__"
    const hasValidYAxis = yAxis && yAxis !== "__placeholder__"
    
    if (chartType === 'pie') {
      return hasValidXAxis && hasValidYAxis
    }
    return hasValidXAxis && hasValidYAxis
  }

  // Auto-apply is disabled - user needs to manually create chart

  const handleCreateChart = async () => {
    console.log('[ChartConfigurationDialog] handleCreateChart called')
    console.log('[ChartConfigurationDialog] canCreateChart():', canCreateChart())
    console.log('[ChartConfigurationDialog] currentUser?.uid:', currentUser?.uid)
    console.log('[ChartConfigurationDialog] currentOrganization?.id:', currentOrganization?.id)
    
    if (!canCreateChart() || !currentUser?.uid || !currentOrganization?.id) {
      console.error('[ChartConfigurationDialog] Missing required data for chart creation')
      
      let errorMsg = 'Cannot create chart: '
      if (!currentUser?.uid) errorMsg += 'You are not signed in. '
      if (!currentOrganization?.id) errorMsg += 'No organization selected. '
      if (!canCreateChart()) errorMsg += 'Chart configuration is incomplete. '
      
      toast({
        title: 'Cannot Create Chart',
        description: errorMsg,
        variant: 'destructive'
      })
      return
    }

    try {
      // Determine if this is a multi-series chart
      const isMultiSeries = groupBy && groupBy !== "__none__"
      
      // Get the chart data and series information
      const processedChartData = chartData
      let seriesInfo = undefined
      
      if (isMultiSeries && processedChartData.length > 0) {
        // Extract series keys from the first data point
        const samplePoint = processedChartData[0]
        const seriesKeys = Object.keys(samplePoint).filter(key => 
          key !== 'name' && typeof samplePoint[key] === 'number'
        )
        
        // Assign colors to each series using the selected color palette
        const paletteColors = colorPalette ? getChartColors(colorPalette, []) : ['#FF8635', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16']
        const seriesColors = seriesKeys.map((_, index) => 
          paletteColors[index % paletteColors.length]
        )
        
        seriesInfo = {
          keys: seriesKeys,
          colors: seriesColors
        }
      }

      const chartConfig: ChartConfig = {
        chartType,
        xAxis: xAxis === "__placeholder__" ? "" : xAxis,
        yAxis: yAxis === "__placeholder__" ? "" : yAxis,
        groupBy: (groupBy && groupBy !== "__none__") ? groupBy : undefined,
        title: title || `${yAxis} by ${xAxis}`,
        colorPalette,
        showLegend,
        // Enhanced multi-series fields
        chartData: processedChartData,
        isMultiSeries,
        seriesInfo
      }

      console.log('[ChartConfigurationDialog] Creating chart directly to database:', chartConfig)
      
      // Save directly to database using the existing saveChartFromConfig function
      await saveChartFromConfig(
        chartConfig,
        data,
        currentOrganization.id,
        currentUser.uid
      )
      
      console.log('[ChartConfigurationDialog] Chart saved successfully to database')
      
      // Show success message
      toast({
        title: 'Chart Created Successfully!',
        description: `${title || `${yAxis} by ${xAxis}`} has been created.`,
      })
      
      // Close the dialog and notify parent to refresh chart list
      onCreateChart(chartConfig)
      
    } catch (error) {
      console.error('[ChartConfigurationDialog] Error creating chart:', error)
      console.error('[ChartConfigurationDialog] Error details:', error.message)
      console.error('[ChartConfigurationDialog] Error stack:', error.stack)
      
      // Show user-friendly error message
      toast({
        title: 'Error Creating Chart',
        description: `There was an error creating your chart: ${error.message || 'Unknown error'}. Please try again.`,
        variant: 'destructive'
      })
    }
  }

  const getColumnRecommendation = (purpose: 'x' | 'y' | 'group') => {
    switch (purpose) {
      case 'x':
        if (chartType === 'line') {
          return columnsByType.date.length > 0 
            ? 'Recommended: Date columns for time series'
            : 'Recommended: Text columns for categories'
        }
        return 'Recommended: Text columns for categories'
      case 'y':
        return 'Recommended: Numeric columns (currency, numbers, percentages)'
      case 'group':
        return 'Optional: Text columns to group data by'
      default:
        return ''
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="w-[1200px] max-w-[95vw] max-h-[95vh] overflow-hidden flex flex-col !max-w-[1200px]"
        style={{ maxWidth: '1200px', width: '1200px' }}
      >
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-3">
                <ChartIcon className="h-6 w-6" style={{ color: config.color }} />
                Configure {config.label}
              </DialogTitle>
              <p className="text-sm text-gray-600 mt-1">{config.description}</p>
            </div>
            
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <Button
                variant={viewMode === 'chart' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('chart')}
                className={`flex items-center gap-2 px-3 py-1.5 ${
                  viewMode === 'chart' 
                    ? 'bg-white shadow-sm text-gray-900' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <ChartIcon className="h-4 w-4" />
                Chart
              </Button>
              <Button
                variant={viewMode === 'data' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('data')}
                className={`flex items-center gap-2 px-3 py-1.5 ${
                  viewMode === 'data' 
                    ? 'bg-white shadow-sm text-gray-900' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Table className="h-4 w-4" />
                Data
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 flex gap-6 py-4 overflow-hidden">
          {/* Configuration Panel */}
          <div className="w-80 flex-shrink-0 space-y-4 overflow-y-auto">
            {/* Chart Title */}
            <div className="space-y-2">
              <Label htmlFor="chart-title">Chart Title</Label>
              <input
                id="chart-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(toTitleCase(e.target.value))}
                placeholder="Enter chart title"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            {/* X-Axis Selection */}
            <div className="space-y-2">
              <Label htmlFor="x-axis">
                {chartType === 'pie' ? 'Category Column' : 'X-Axis (Categories)'}
              </Label>
              <Select value={xAxis || "__placeholder__"} onValueChange={(value) => setXAxis(value === "__placeholder__" ? "" : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select column for categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__placeholder__">
                    <span className="text-gray-500">Select column for categories</span>
                  </SelectItem>
                  {headers.map(header => (
                    <SelectItem key={header} value={header}>
                      <div className="flex items-center gap-2">
                        <span>{header}</span>
                        <span className="text-xs text-gray-500">
                          ({columnTypes[header]?.type || 'text'})
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">{getColumnRecommendation('x')}</p>
            </div>

            {/* Y-Axis Selection */}
            <div className="space-y-2">
              <Label htmlFor="y-axis">
                {chartType === 'pie' ? 'Value Column' : 'Y-Axis (Values)'}
              </Label>
              <Select value={yAxis || "__placeholder__"} onValueChange={(value) => setYAxis(value === "__placeholder__" ? "" : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select column for values" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__placeholder__">
                    <span className="text-gray-500">Select column for values</span>
                  </SelectItem>
                  {headers.map(header => (
                    <SelectItem key={header} value={header}>
                      <div className="flex items-center gap-2">
                        <span>{header}</span>
                        <span className="text-xs text-gray-500">
                          ({columnTypes[header]?.type || 'text'})
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">{getColumnRecommendation('y')}</p>
            </div>

            {/* Group By (optional) */}
            {chartType !== 'pie' && (
              <div className="space-y-2">
                <Label htmlFor="group-by">Group By (Optional)</Label>
                <Select value={groupBy || "__none__"} onValueChange={(value) => setGroupBy(value === "__none__" ? "" : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select column to group by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">
                      <span className="text-gray-500">No grouping</span>
                    </SelectItem>
                    {headers.filter(h => h !== xAxis && h !== yAxis).map(header => (
                      <SelectItem key={header} value={header}>
                        <div className="flex items-center gap-2">
                          <span>{header}</span>
                          <span className="text-xs text-gray-500">
                            ({columnTypes[header]?.type || 'text'})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">{getColumnRecommendation('group')}</p>
              </div>
            )}

            {/* Color Palette Selection */}
            <div className="space-y-2">
              <Label>Color Palette</Label>
              {currentUser && currentOrganization ? (
                <ColorPaletteSelector
                  selectedPaletteId={colorPalette}
                  onPaletteChange={setColorPalette}
                  userId={currentUser.uid}
                  organizationId={currentOrganization.id}
                  showManagementButtons={false}
                  className="max-h-48 overflow-y-auto"
                />
              ) : (
                <div className="space-y-2">
                  <div className="text-sm text-gray-500 mb-2">
                    Using default color palette
                  </div>
                  <div className="flex items-center gap-2 p-2 border rounded-lg bg-gray-50">
                    <div className="w-4 h-4 bg-orange-500 rounded"></div>
                    <span className="text-sm">FireForge Default</span>
                  </div>
                </div>
              )}
            </div>

            {/* Show Legend Toggle */}
            <div className="space-y-2">
              <Label>Display Options</Label>
              <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                <div className="flex items-center gap-2">
                  {showLegend ? (
                    <Eye className="h-4 w-4 text-gray-600" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  )}
                  <span className="text-sm font-medium">Show Legend</span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowLegend(!showLegend)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    showLegend ? 'bg-orange-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      showLegend ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Chart Description */}
            {canCreateChart() && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg border text-sm">
                <p className="text-gray-700">
                  {chartType === 'pie' 
                    ? `Pie chart showing ${yAxis} grouped by ${xAxis}`
                    : `${config.label} with ${xAxis} on X-axis and ${yAxis} on Y-axis`
                  }
                  {groupBy && groupBy !== '__none__' && ` (grouped by ${groupBy})`}
                </p>
                {groupBy && groupBy !== '__none__' && (
                  <p className="text-xs text-blue-600 mt-1">
                    Grouped chart showing separate series for each {groupBy} value.
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Data points: {data.length} rows
                </p>
              </div>
            )}

          </div>

          {/* Preview Panel */}
          <div className="flex-1 bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
            {viewMode === 'chart' ? (
              <div className="h-full">
                {canCreateChart() ? (
                  <div className="h-full p-4">
                    <UniversalChartRenderer 
                      chart={{
                        title: title || 'Chart Preview',
                        data: chartData,
                        chartType: chartType,
                        xAxis: xAxis,
                        yAxis: yAxis,
                        configuration: {
                          colorPalette: colorPalette,
                          singleColor: '#FF8635'
                        },
                        displayOptions: {
                          showGridLines: true,
                          showLegend: showLegend
                        }
                      }}
                      height={Math.max(350, window.innerHeight * 0.45)}
                    />
                  </div>
                ) : (
                  <div className="text-center text-gray-400">
                    <ChartIcon className="h-12 w-12 mx-auto mb-2" />
                    <p>Configure your chart settings to see preview</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-full overflow-hidden">
                <DataTableViewer
                  initialData={data}
                  showHeaderOnly={false}
                  showTableOnly={true}
                  className="h-full"
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateChart}
            disabled={!canCreateChart()}
            style={{ backgroundColor: '#FF8635', borderColor: '#FF8635' }}
            className="text-white hover:opacity-90"
            title={!canCreateChart() ? "Please select both X-axis and Y-axis columns" : ""}
          >
            <ChartIcon className="h-4 w-4 mr-2" />
            Create {config.label}
          </Button>
          {!canCreateChart() && (
            <p className="text-xs text-gray-500 mt-1">
              Select columns for both axes to create chart
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}